import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Briefcase, GraduationCap, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo, LangToggle } from "@/components/Brand";
import { api, formatApiError } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const USES = [["organisation", Building2, "Organisasi / Pejabat", "Organisation / Office"], ["sme", Briefcase, "SME / Perniagaan", "SME / Business"], ["education", GraduationCap, "Pensyarah / Pelajar", "Lecturer / Student"], ["personal", User, "Kegunaan peribadi", "Personal use"]];

export default function Onboarding() {
  const { L, lang, setLang } = useApp();
  const { refresh, selectWs } = useAuth();
  const navigate = useNavigate();
  const [use, setUse] = useState("organisation");
  const [tl, setTl] = useState("auto");
  const [ol, setOl] = useState("ms");
  const [team, setTeam] = useState("");

  const finish = async () => {
    try {
      await api.patch("/auth/me", { use_case: use, transcription_language: tl, output_language: ol, ui_language: lang, onboarded: true });
      if (team.trim()) {
        const { data } = await api.post("/workspaces", { name: team.trim() });
        selectWs(data.id);
      }
      await refresh();
      navigate("/app");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="onboarding-page">
      <header className="flex items-center justify-between px-6 py-4"><Logo /><LangToggle /></header>
      <main className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="text-3xl font-extrabold tracking-tight">{L("Mari sediakan ruang kerja anda", "Let's set up your workspace")}</h1>
        <p className="mt-2 text-slate-500">{L("Ini membantu kami memilih template minit yang sesuai.", "This helps us pick suitable minute templates.")}</p>
        <h2 className="mt-8 text-sm font-bold">{L("1. Anda menggunakan MinitAI untuk…", "1. You'll use MinitAI for…")}</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {USES.map(([k, Icon, ms, en]) => (
            <button key={k} onClick={() => setUse(k)} aria-pressed={use === k} data-testid={`use-case-${k}`} className={`flex items-center gap-3 rounded-2xl border bg-white p-4 text-left text-sm font-semibold transition-colors ${use === k ? "border-violet-500 ring-1 ring-violet-500" : "border-slate-200 hover:border-violet-300"}`}>
              <Icon size={18} className="text-violet-600" /> {L(ms, en)}
            </button>
          ))}
        </div>
        <h2 className="mt-8 text-sm font-bold">{L("2. Bahasa", "2. Languages")}</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Select value={lang} onValueChange={setLang}><SelectTrigger className="h-10 rounded-xl bg-white" data-testid="onb-ui-lang"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ms">UI: Bahasa Melayu</SelectItem><SelectItem value="en">UI: English</SelectItem></SelectContent></Select>
          <Select value={tl} onValueChange={setTl}><SelectTrigger className="h-10 rounded-xl bg-white" data-testid="onb-transcription-lang"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="auto">{L("Transkripsi: Auto/campuran", "Transcription: Auto/mixed")}</SelectItem>{LANGS.map(([k, ms, en]) => <SelectItem key={k} value={k}>{L("Transkripsi", "Transcription")}: {L(ms, en)}</SelectItem>)}</SelectContent></Select>
          <Select value={ol} onValueChange={setOl}><SelectTrigger className="h-10 rounded-xl bg-white" data-testid="onb-output-lang"><SelectValue /></SelectTrigger><SelectContent>{LANGS.map(([k, ms, en]) => <SelectItem key={k} value={k}>{L("Hasil", "Output")}: {L(ms, en)}</SelectItem>)}</SelectContent></Select>
        </div>
        <h2 className="mt-8 text-sm font-bold">{L("3. Cipta workspace pasukan (pilihan)", "3. Create a team workspace (optional)")}</h2>
        <Input value={team} onChange={(e) => setTeam(e.target.value)} placeholder={L("cth. Unit Pentadbiran", "e.g. Admin Unit")} className="mt-3 h-11 rounded-xl bg-white" data-testid="onb-team-input" />
        <Button onClick={finish} className="mt-10 h-11 rounded-xl bg-violet-600 px-8 hover:bg-violet-700" data-testid="onboarding-finish-btn">{L("Mula guna", "Get started")}</Button>
      </main>
    </div>
  );
}
